import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog } from '@/features/dialogs/common/types'
import { MarketInfo } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { getDepositMaxValue } from '@/features/sparklend/common/utils/action-max-value-getters/getDepositMaxValue'
import {
  depositValidationIssueToMessage,
  validateDeposit,
} from '@/features/sparklend/common/validation/validateDeposit'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getDepositDialogFormValidator(walletInfo: MarketWalletInfo, marketInfo: MarketInfo) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const value = NormalizedNumber(field.value === '' ? '0' : field.value)
    const balance = walletInfo.findWalletBalanceForSymbol(field.symbol)
    const supplyingReserve = marketInfo.findOneReserveBySymbol(field.symbol)

    const issue = validateDeposit({
      value,
      asset: {
        status: supplyingReserve.status,
        totalLiquidity: supplyingReserve.totalLiquidity,
        supplyCap: supplyingReserve.supplyCap,
      },
      user: { balance, alreadyDepositedValueUSD: NormalizedNumber('0') },
    })
    if (issue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: depositValidationIssueToMessage[issue],
        path: ['value'],
      })
    }
  })
}

export interface GetFormFieldsForDepositDialogArgs {
  form: UseFormReturn<AssetInputSchema>
  marketInfo: MarketInfo
  walletInfo: MarketWalletInfo
}
export function getFormFieldsForDepositDialog({
  form,
  marketInfo,
  walletInfo,
}: GetFormFieldsForDepositDialogArgs): FormFieldsForDialog {
  // eslint-disable-next-line func-style
  const changeAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('symbol', newSymbol)
    form.setValue('value', '')
    form.setValue('isMaxSelected', false)
    form.clearErrors()
  }

  const { symbol, value } = form.getValues()
  const position = marketInfo.findOnePositionBySymbol(symbol)
  const token = marketInfo.findOneBySymbol(symbol)
  const balance = walletInfo.findWalletBalanceForSymbol(symbol)

  const maxValue = getDepositMaxValue({
    asset: {
      status: position.reserve.status,
      totalLiquidity: position.reserve.totalLiquidity,
      isNativeAsset: marketInfo.nativeAssetInfo.nativeAssetSymbol === symbol,
      supplyCap: position.reserve.supplyCap,
    },
    user: {
      balance,
    },
    chain: {
      minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    },
  })

  return {
    selectedAsset: token,
    selectedAssetBalance: balance,
    value,
    maxSelectedFieldName: 'isMaxSelected',
    changeAsset,
    maxValue,
  }
}
